import { useCallback, useState } from 'react';
import { localTime } from '@helpers';
import { ProductionBatchStatistics } from '@interfaces/search';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { useFocusEffect } from '@react-navigation/native';
import { BatchTimelineProps } from '@v2/pages/batch/details/modules/batch/components/BatchTimeline/BatchTimeline';

interface UseBatchTimelineRangeParams extends Pick<BatchTimelineProps, 'shouldSetEndTimeAsNow'> {
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics>;
}

export const useBatchTimelineRange = ({ batch, shouldSetEndTimeAsNow }: UseBatchTimelineRangeParams) => {
  const [productionBatchStartDateForDisplay, setProductionBatchStartDateForDisplay] = useState<string | null>(null);
  const [productionBatchEndDateForDisplay, setProductionBatchEndDateForDisplay] = useState<string | null>(null);
  const [productionBatchStartDate, setProductionBatchStartDate] = useState<string | null>(null);
  const [productionBatchEndDate, setProductionBatchEndDate] = useState<string | null>(null);

  // Set initial batch dates when batch data loads
  useFocusEffect(
    useCallback(() => {
      if (!batch?.data) return;

      const now = localTime().format();
      const start = batch?.data?.startedOn ? localTime(batch?.data?.startedOn).format() : now;

      const end =
        shouldSetEndTimeAsNow || batch.data?.isInProgress
          ? now
          : batch?.data?.endedOn
          ? localTime(batch?.data?.endedOn).format()
          : localTime(start).add(1, 'h').format();

      setProductionBatchStartDate(start);
      setProductionBatchStartDateForDisplay(start);
      setProductionBatchEndDate(end);
      setProductionBatchEndDateForDisplay(end);
    }, [batch?.data?.startedOn, batch?.data?.endedOn, batch.data?.isInProgress, shouldSetEndTimeAsNow])
  );

  const isEndDateForDisplayAndActualEndDateIsTheSame =
    !productionBatchEndDateForDisplay ||
    localTime(productionBatchEndDateForDisplay).isAfter(localTime(productionBatchEndDate)) ||
    localTime(productionBatchEndDateForDisplay).isSame(localTime(productionBatchEndDate));

  const isStartDateForDisplayAndActualStartDateIsTheSame =
    !productionBatchStartDateForDisplay ||
    localTime(productionBatchStartDateForDisplay).isBefore(localTime(productionBatchStartDate)) ||
    localTime(productionBatchStartDateForDisplay).isSame(localTime(productionBatchStartDate), 'm');

  const isZoomResetDisabled = isEndDateForDisplayAndActualEndDateIsTheSame && isStartDateForDisplayAndActualStartDateIsTheSame;

  const isZoomOutDisabled =
    Math.abs(localTime(productionBatchStartDateForDisplay).diff(localTime(productionBatchEndDateForDisplay), 's')) >=
    Math.abs(localTime(productionBatchStartDate).diff(localTime(productionBatchEndDate), 's'));

  return {
    productionBatchStartDateForDisplay,
    setProductionBatchStartDateForDisplay,
    productionBatchEndDateForDisplay,
    setProductionBatchEndDateForDisplay,
    productionBatchStartDate,
    setProductionBatchStartDate,
    productionBatchEndDate,
    setProductionBatchEndDate,
    isEndDateForDisplayAndActualEndDateIsTheSame,
    isStartDateForDisplayAndActualStartDateIsTheSame,
    isZoomResetDisabled,
    isZoomOutDisabled,
  };
};
